import { FC, Fragment, ReactNode, useEffect } from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import { ReactComponent as BackArrow } from 'src/assets/svg/back-arrow.svg';
import { appConfig } from 'src/app';
import { routes } from 'src/constants/routes';
import { useFetchRoomInfo } from 'src/lib/hooks/use-fetch-room-info';
import { useGetDevelopmentDetails } from 'src/lib/hooks/use-get-development-details';
import { useHandleQueryError } from 'src/lib/hooks/use-handle-query-error';
import { parseMoveInDate } from 'src/utils/parse-move-in-date';
import { getStayLength } from 'src/utils/get-stay-length';
import { buildRoomsUrl } from 'src/utils/get-stl-rooms-url';
import { formatAmount } from 'src/utils/format-amount';
import { formatFloorNumber } from 'src/utils/validators/format-floor';
import { validateStaySelection } from 'src/utils/validators/validate-stay-selection';
import { getTheme } from 'src/utils/getTheme';

import { FailurePage } from '../failure';

import { styles } from './styles';
import { RoomImageCarousel } from './room-image-carousel';
import { CostBreakdown } from './cost-breakdown';

const { isHosTheme, brand } = getTheme();

const DWS_DEVELOPMENT_SLUGS = [
    'first-street',
    'vita-student-at-strawberry-place-newcastle',
    'vita-student-leeds',
    'vita-student-glasgow',
    'vita-student-manchester-circle-square',
    'vita-student-edinburgh',
    'vita-student-birmingham',
];
const MINIMUM_STAY_DURATION = Number(appConfig.roomsAvailabilityMinimumStayDuration);
const MINIMUM_STAY_DURATION_DWS = Number(appConfig.roomsAvailabilityMinimumStayDurationDws);

export const ViewRoomPage: FC = () => {
    const { t, i18n } = useTranslation();
    const [searchParams] = useSearchParams();
    const handleQueryError = useHandleQueryError();

    const development = searchParams.get('development') || '';
    const roomCode = searchParams.get('roomCode') || '';
    const academicYear = searchParams.get('academicYear') || '';
    const moveInDate = parseMoveInDate(searchParams.get('moveInDate'));
    // Either a duration or a move out date, never both
    const stay = getStayLength(searchParams.get('duration'), searchParams.get('moveOutDate'));

    const roomKey = [roomCode, academicYear, moveInDate, stay?.duration, stay?.moveOutDate].join(
        '|'
    );

    // The rooms page only searches by duration, so a move out date stay goes back to an empty search
    let roomsUrl = buildRoomsUrl(development, academicYear);

    if (stay?.duration) {
        roomsUrl = buildRoomsUrl(development, academicYear, {
            startDate: moveInDate,
            duration: stay.duration,
        });
    }

    const hasRequiredParams = Boolean(roomCode && development && academicYear && moveInDate);

    const {
        data: developmentDetails,
        isError: isDevelopmentError,
        isLoading: isLoadingDevelopment,
    } = useGetDevelopmentDetails(development || null, academicYear || null);

    const stayErrorKey = validateStaySelection({
        moveInDate: moveInDate || null,
        duration: stay?.duration ? String(stay.duration) : null,
        requestedMoveOutDate: stay?.moveOutDate,
        moveOutDate: developmentDetails?.move_out_date,
        minimumStayDuration: DWS_DEVELOPMENT_SLUGS.includes(development ?? '')
            ? MINIMUM_STAY_DURATION_DWS
            : MINIMUM_STAY_DURATION,
    });

    const {
        data: roomInfo,
        isLoading: isLoadingRoomInfo,
        error,
    } = useFetchRoomInfo({
        developmentSlug: development,
        roomCode,
        academicYear,
        moveInDate,
        stay,
        // Only fetch once the stay has been validated against the development's move out date
        enabled: Boolean(developmentDetails?.move_out_date) && !stayErrorKey,
    });

    const isLoading = isLoadingDevelopment || isLoadingRoomInfo;

    useEffect(() => {
        if (error) handleQueryError(error, academicYear, false, roomsUrl);
    }, [error]);

    if (!hasRequiredParams || !stay) return <Navigate to={routes.failure.index} replace />;

    if (isDevelopmentError || stayErrorKey || (!isLoadingDevelopment && !developmentDetails)) {
        return <FailurePage />;
    }

    const handleBackToResults = () => {
        if (window.history.length > 1) {
            window.history.back();
            return;
        }

        const { vs, hos } = appConfig.brochureSiteUrl;
        window.location.assign((isHosTheme ? hos : vs) || '/');
    };

    const renderInfoValue = (getValue: (info: NonNullable<typeof roomInfo>) => ReactNode) =>
        roomInfo ? getValue(roomInfo) : '-';

    const roomInfoItems = [
        { label: t('view_room_room_code'), value: roomCode },
        {
            label: t('room_card_floor'),
            value: renderInfoValue(({ floor }) => (
                <span
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: formatFloorNumber(floor, i18n.language, t),
                    }}
                />
            )),
        },
        {
            label: t('view_room_size'),
            value: renderInfoValue(({ size }) => t('view_room_size_sqm', { size })),
        },
        { label: t('view_room_academic_year'), value: academicYear },
        {
            label: t('view_room_weekly_price'),
            value: renderInfoValue(({ basePrice, currency }) =>
                t('view_room_per_week', { price: formatAmount(basePrice.toFixed(2), currency) })
            ),
        },
    ];

    return (
        <Box css={styles.container}>
            <Box css={styles.header}>
                <Box css={styles.headerContent}>
                    {/* Move out date links come straight from outside the booking flow, so there are no
                        results to go back to */}
                    {!stay.moveOutDate && (
                        <button type="button" css={styles.backButton} onClick={handleBackToResults}>
                            <BackArrow css={styles.backIcon} />
                            <Typography component="span">
                                {t('view_room_back_to_results')}
                            </Typography>
                        </button>
                    )}
                    <Box css={styles.titleBlock}>
                        {isLoading ? (
                            <Fragment>
                                <Skeleton variant="text" css={styles.subtitleSkeleton} />
                                <Skeleton variant="text" css={styles.titleSkeleton} />
                            </Fragment>
                        ) : (
                            <Fragment>
                                {development && (
                                    <Typography variant="h4" css={styles.subtitle}>
                                        {developmentDetails?.name}
                                    </Typography>
                                )}
                                {roomInfo?.unitType && (
                                    <Typography variant="h1" css={styles.title}>
                                        {roomInfo.unitType}
                                    </Typography>
                                )}
                            </Fragment>
                        )}
                    </Box>

                    <Box component="dl" css={styles.infoRow} aria-busy={isLoading}>
                        {roomInfoItems.map(({ label, value }) => (
                            <Box key={label} css={styles.infoItem}>
                                <Typography component="dt" variant="body3" css={styles.infoLabel}>
                                    {isLoading ? (
                                        <Skeleton variant="text" css={styles.infoLabelSkeleton} />
                                    ) : (
                                        label
                                    )}
                                </Typography>
                                <Typography component="dd" variant="h4" css={styles.infoValue}>
                                    {isLoading ? (
                                        <Skeleton variant="text" css={styles.infoValueSkeleton} />
                                    ) : (
                                        value
                                    )}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                </Box>
            </Box>

            <Box css={styles.content}>
                <Box css={styles.carouselColumn}>
                    {isLoading ? (
                        <Skeleton variant="rectangular" css={styles.carouselSkeleton} />
                    ) : (
                        <RoomImageCarousel
                            key={roomKey}
                            images={(roomInfo?.imagery ?? []).map(
                                ({ cdnUrl, thumbnailUrl }, index) => ({
                                    src: cdnUrl,
                                    // Fall back to the full image if a thumbnail is missing
                                    thumbnailSrc: thumbnailUrl || cdnUrl,
                                    alt: t('view_room_image_alt', { number: index + 1 }),
                                })
                            )}
                        />
                    )}
                    {isLoading ? (
                        <Box css={styles.imageDisclaimer}>
                            <Skeleton variant="text" />
                            <Skeleton variant="text" width="60%" />
                        </Box>
                    ) : (
                        <Typography variant="body4" component="p" css={styles.imageDisclaimer}>
                            {t('view_room_image_disclaimer', { brand })}
                        </Typography>
                    )}
                </Box>
                <Box css={styles.breakdownColumn}>
                    <CostBreakdown
                        key={roomKey}
                        roomCode={roomCode}
                        roomsUrl={roomsUrl}
                        academicYear={academicYear}
                        moveInDate={moveInDate}
                        stay={stay}
                        roomInfo={roomInfo}
                        loading={isLoading}
                        hasError={Boolean(error)}
                    />
                </Box>
            </Box>
        </Box>
    );
};
